import React from 'react';
import {
  CLOTHING_AUDIENCES,
  getClothingTypes,
  getClothingSubtypes,
  getClothingStyles,
  isClothingListingCategory,
} from '../../constants/clothingCategorySchema';
import {
  WATCH_DEPARTMENTS,
  getWatchSubcategories,
  isWatchesListingCategory,
} from '../../constants/watchesCategorySchema';
import {
  BAG_DEPARTMENTS,
  getBagSubcategories,
  isBagsListingCategory,
} from '../../constants/bagsCategorySchema';
import {
  FOOTWEAR_DEPARTMENTS,
  getFootwearSubcategories,
  isFootwearListingCategory,
} from '../../constants/footwearCategorySchema';
import {
  ACCESSORY_DEPARTMENTS,
  getAccessorySubcategories,
  isAccessoriesListingCategory,
} from '../../constants/accessoriesCategorySchema';
import {
  KITCHEN_DINING_GROUPS,
  KITCHEN_DINING_ROOT,
  getKitchenDiningTypes,
  isHomeKitchenListingCategory,
  isKitchenDiningSubcategory,
} from '../../constants/kitchenDiningCategorySchema';
import {
  getGenericSubcategories,
  getTaxonomyResetPatch,
} from '../../constants/listingCategoryTaxonomy';

/**
 * Reusable cascading category / subcategory / sub-subcategory dropdowns.
 *
 * Renders the exact same taxonomy tree used by the luxury & pre-owned listing
 * flow. Presentation is delegated to the host form via the `ui` prop so the
 * dropdowns inherit the surrounding page styling.
 *
 * Props:
 *  - value:      object holding taxonomy fields (category, subcategory, watch_*, etc.)
 *  - onChange:   (patchObject) => void  — merges a partial update into the form state
 *  - errors:     object keyed by field name
 *  - categories: array of top-level category strings to offer
 *  - ui:         { FormGroup, Label, Select, ErrorText } styled components
 *  - categoryLabel: optional (value) => label mapper for the category options
 */
const ListingCategorySelect = ({
  value = {},
  onChange,
  errors = {},
  categories = [],
  ui,
  categoryLabel,
}) => {
  const { FormGroup, Label, Select, ErrorText } = ui;
  const category = value.category || '';
  const labelFor = categoryLabel || ((c) => c);
  const patch = (fields) => onChange && onChange(fields);

  const genericSubcategories = getGenericSubcategories(category);
  const showGenericSubcategory =
    genericSubcategories.length > 0 &&
    !isWatchesListingCategory(category) &&
    !isBagsListingCategory(category) &&
    !isFootwearListingCategory(category) &&
    !isAccessoriesListingCategory(category) &&
    !isClothingListingCategory(category);

  return (
    <>
      <FormGroup>
        <Label htmlFor="category">Category *</Label>
        <Select
          id="category"
          value={category}
          onChange={(e) => patch({ ...getTaxonomyResetPatch(), category: e.target.value })}
        >
          <option value="">Select a category</option>
          {categories.map((c) => (
            <option key={c} value={c}>{labelFor(c)}</option>
          ))}
        </Select>
        {errors.category && <ErrorText>{errors.category}</ErrorText>}
      </FormGroup>

      {showGenericSubcategory && (
        <FormGroup>
          <Label htmlFor="subcategory">Subcategory *</Label>
          <Select
            id="subcategory"
            value={
              isKitchenDiningSubcategory(value.subcategory)
                ? KITCHEN_DINING_ROOT
                : value.subcategory || ''
            }
            onChange={(e) => patch({ subcategory: e.target.value, kitchen_group: '', kitchen_type: '' })}
          >
            <option value="">Select a subcategory</option>
            {genericSubcategories.map((sub) => (
              <option key={sub} value={sub}>{sub}</option>
            ))}
          </Select>
          {errors.subcategory && <ErrorText>{errors.subcategory}</ErrorText>}
        </FormGroup>
      )}

      {isHomeKitchenListingCategory(category) && isKitchenDiningSubcategory(value.subcategory) && (
        <>
          <FormGroup>
            <Label htmlFor="kitchen_group">Kitchen category *</Label>
            <Select
              id="kitchen_group"
              value={value.kitchen_group || ''}
              onChange={(e) => patch({ kitchen_group: e.target.value, kitchen_type: '' })}
            >
              <option value="">Select kitchen category</option>
              {KITCHEN_DINING_GROUPS.map((group) => (
                <option key={group} value={group}>{group}</option>
              ))}
            </Select>
            {errors.kitchen_group && <ErrorText>{errors.kitchen_group}</ErrorText>}
          </FormGroup>

          {value.kitchen_group && (
            <FormGroup>
              <Label htmlFor="kitchen_type">Kitchen type *</Label>
              <Select
                id="kitchen_type"
                value={value.kitchen_type || ''}
                onChange={(e) => patch({ kitchen_type: e.target.value })}
              >
                <option value="">Select kitchen type</option>
                {getKitchenDiningTypes(value.kitchen_group).map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </Select>
              {errors.kitchen_type && <ErrorText>{errors.kitchen_type}</ErrorText>}
            </FormGroup>
          )}
        </>
      )}

      {isWatchesListingCategory(category) && (
        <>
          <FormGroup>
            <Label htmlFor="watch_department">Department *</Label>
            <Select
              id="watch_department"
              value={value.watch_department || ''}
              onChange={(e) => patch({ watch_department: e.target.value, watch_subcategory: '' })}
            >
              <option value="">Select department</option>
              {WATCH_DEPARTMENTS.map((dept) => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </Select>
            {errors.watch_department && <ErrorText>{errors.watch_department}</ErrorText>}
          </FormGroup>

          {value.watch_department && (
            <FormGroup>
              <Label htmlFor="watch_subcategory">Subcategory *</Label>
              <Select
                id="watch_subcategory"
                value={value.watch_subcategory || ''}
                onChange={(e) => patch({ watch_subcategory: e.target.value })}
              >
                <option value="">Select subcategory</option>
                {getWatchSubcategories(value.watch_department).map((sub) => (
                  <option key={sub} value={sub}>{sub}</option>
                ))}
              </Select>
              {errors.watch_subcategory && <ErrorText>{errors.watch_subcategory}</ErrorText>}
            </FormGroup>
          )}
        </>
      )}

      {isBagsListingCategory(category) && (
        <>
          <FormGroup>
            <Label htmlFor="bag_department">Department *</Label>
            <Select
              id="bag_department"
              value={value.bag_department || ''}
              onChange={(e) => patch({ bag_department: e.target.value, bag_subcategory: '' })}
            >
              <option value="">Select department</option>
              {BAG_DEPARTMENTS.map((dept) => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </Select>
            {errors.bag_department && <ErrorText>{errors.bag_department}</ErrorText>}
          </FormGroup>

          {value.bag_department && (
            <FormGroup>
              <Label htmlFor="bag_subcategory">Subcategory *</Label>
              <Select
                id="bag_subcategory"
                value={value.bag_subcategory || ''}
                onChange={(e) => patch({ bag_subcategory: e.target.value })}
              >
                <option value="">Select subcategory</option>
                {getBagSubcategories(value.bag_department).map((sub) => (
                  <option key={sub} value={sub}>{sub}</option>
                ))}
              </Select>
              {errors.bag_subcategory && <ErrorText>{errors.bag_subcategory}</ErrorText>}
            </FormGroup>
          )}
        </>
      )}

      {isFootwearListingCategory(category) && (
        <>
          <FormGroup>
            <Label htmlFor="footwear_department">Department *</Label>
            <Select
              id="footwear_department"
              value={value.footwear_department || ''}
              onChange={(e) => patch({ footwear_department: e.target.value, footwear_subcategory: '' })}
            >
              <option value="">Select department</option>
              {FOOTWEAR_DEPARTMENTS.map((dept) => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </Select>
            {errors.footwear_department && <ErrorText>{errors.footwear_department}</ErrorText>}
          </FormGroup>

          {value.footwear_department && (
            <FormGroup>
              <Label htmlFor="footwear_subcategory">Subcategory *</Label>
              <Select
                id="footwear_subcategory"
                value={value.footwear_subcategory || ''}
                onChange={(e) => patch({ footwear_subcategory: e.target.value })}
              >
                <option value="">Select subcategory</option>
                {getFootwearSubcategories(value.footwear_department).map((sub) => (
                  <option key={sub} value={sub}>{sub}</option>
                ))}
              </Select>
              {errors.footwear_subcategory && <ErrorText>{errors.footwear_subcategory}</ErrorText>}
            </FormGroup>
          )}
        </>
      )}

      {isAccessoriesListingCategory(category) && (
        <>
          <FormGroup>
            <Label htmlFor="accessory_department">Department *</Label>
            <Select
              id="accessory_department"
              value={value.accessory_department || ''}
              onChange={(e) => patch({ accessory_department: e.target.value, accessory_subcategory: '' })}
            >
              <option value="">Select department</option>
              {ACCESSORY_DEPARTMENTS.map((dept) => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </Select>
            {errors.accessory_department && <ErrorText>{errors.accessory_department}</ErrorText>}
          </FormGroup>

          {value.accessory_department && (
            <FormGroup>
              <Label htmlFor="accessory_subcategory">Subcategory *</Label>
              <Select
                id="accessory_subcategory"
                value={value.accessory_subcategory || ''}
                onChange={(e) => patch({ accessory_subcategory: e.target.value })}
              >
                <option value="">Select subcategory</option>
                {getAccessorySubcategories(value.accessory_department).map((sub) => (
                  <option key={sub} value={sub}>{sub}</option>
                ))}
              </Select>
              {errors.accessory_subcategory && <ErrorText>{errors.accessory_subcategory}</ErrorText>}
            </FormGroup>
          )}
        </>
      )}

      {isClothingListingCategory(category) && (
        <>
          <FormGroup>
            <Label htmlFor="clothing_audience">Department *</Label>
            <Select
              id="clothing_audience"
              value={value.clothing_audience || ''}
              onChange={(e) => patch({
                clothing_audience: e.target.value,
                clothing_type: '',
                clothing_subtype: '',
                clothing_style: '',
              })}
            >
              <option value="">Select department</option>
              {CLOTHING_AUDIENCES.map((audience) => (
                <option key={audience} value={audience}>{audience}</option>
              ))}
            </Select>
            {errors.clothing_audience && <ErrorText>{errors.clothing_audience}</ErrorText>}
          </FormGroup>

          {value.clothing_audience && (
            <FormGroup>
              <Label htmlFor="clothing_type">Clothing type *</Label>
              <Select
                id="clothing_type"
                value={value.clothing_type || ''}
                onChange={(e) => patch({
                  clothing_type: e.target.value,
                  clothing_subtype: '',
                  clothing_style: '',
                })}
              >
                <option value="">Select clothing type</option>
                {getClothingTypes(value.clothing_audience).map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </Select>
              {errors.clothing_type && <ErrorText>{errors.clothing_type}</ErrorText>}
            </FormGroup>
          )}

          {value.clothing_type && (
            <FormGroup>
              <Label htmlFor="clothing_subtype">Subtype *</Label>
              <Select
                id="clothing_subtype"
                value={value.clothing_subtype || ''}
                onChange={(e) => patch({ clothing_subtype: e.target.value, clothing_style: '' })}
              >
                <option value="">Select subtype</option>
                {getClothingSubtypes(value.clothing_audience, value.clothing_type).map((subtype) => (
                  <option key={subtype} value={subtype}>{subtype}</option>
                ))}
              </Select>
              {errors.clothing_subtype && <ErrorText>{errors.clothing_subtype}</ErrorText>}
            </FormGroup>
          )}

          {value.clothing_subtype && (
            <FormGroup>
              <Label htmlFor="clothing_style">Style *</Label>
              <Select
                id="clothing_style"
                value={value.clothing_style || ''}
                onChange={(e) => patch({ clothing_style: e.target.value })}
              >
                <option value="">Select style</option>
                {getClothingStyles(
                  value.clothing_audience,
                  value.clothing_type,
                  value.clothing_subtype,
                ).map((style) => (
                  <option key={style} value={style}>{style}</option>
                ))}
              </Select>
              {errors.clothing_style && <ErrorText>{errors.clothing_style}</ErrorText>}
            </FormGroup>
          )}
        </>
      )}
    </>
  );
};

export default ListingCategorySelect;
